import { FC } from 'react';
import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import type { Trim, TrimPercentageQuestion } from '../../types';
import { formatPricing } from '../../utils/pricing';
import { getItemSpecifications } from './wizard-utils';
import { SpecField } from './spec-field';
import type { TrimPercentageSelection } from './types';
import { useQuestionnaireResponse } from './questionnaire-response-context';

interface TrimPercentageWizardProps {
  question: TrimPercentageQuestion;
}

/**
 * Renders the interactive body for a TrimPercentage question.
 * Users add trims and distribute percentages across them.
 * @param root0
 * @param root0.question
 */
export const TrimPercentageWizard: FC<TrimPercentageWizardProps> = ({
  question,
}) => {
  const {
    catalog,
    answers,
    itemSpecValues,
    handleAnswerChange,
    handleSpecChange,
  } = useQuestionnaireResponse();
  const answer = (answers[question.id] as TrimPercentageSelection[]) ?? [];
  const specValues = itemSpecValues[question.id] ?? {};

  const onAnswerChange = (value: TrimPercentageSelection[]) =>
    handleAnswerChange(question.id, value);
  const onSpecChange = (selectionId: string, specId: string, v: string) =>
    handleSpecChange(question.id, selectionId, specId, v);

  const availableTrims = question.availableTrimIds
    .map((id) => catalog.trim.find((t) => t.id === id))
    .filter(Boolean) as Trim[];

  const totalPercentage = answer.reduce(
    (sum, s) => sum + (s.percentage || 0),
    0,
  );

  const handleTrimSelect = (trimId: string) => {
    const currentTotal = answer.reduce(
      (sum, s) => sum + (s.percentage || 0),
      0,
    );
    const totalItems = answer.length + 1;
    const idealPercentage = 100 / totalItems;

    let newPercentage = idealPercentage;
    let adjustedExisting = [...answer];

    if (currentTotal > 0) {
      const remainingForExisting = 100 - idealPercentage;
      adjustedExisting = answer.map((s) => ({
        ...s,
        percentage: (s.percentage / currentTotal) * remainingForExisting,
      }));
      const newTotal = adjustedExisting.reduce(
        (sum, s) => sum + s.percentage,
        0,
      );
      newPercentage = 100 - newTotal;
    }

    // Round to whole numbers and correct for rounding drift
    const allSelections = [...adjustedExisting, { percentage: newPercentage }];
    const rounded = allSelections.map((s) => ({
      ...s,
      percentage: Math.floor(s.percentage),
    }));
    const totalRounded = rounded.reduce((sum, s) => sum + s.percentage, 0);
    const remainder = 100 - totalRounded;
    if (remainder > 0 && rounded.length > 0) {
      rounded[0].percentage += remainder;
    }

    const newSelection: TrimPercentageSelection = {
      id: crypto.randomUUID(),
      trimId,
      percentage: rounded[rounded.length - 1].percentage,
    };

    const finalExisting = rounded.slice(0, -1).map((item, i) => ({
      ...adjustedExisting[i],
      percentage: item.percentage,
    }));

    onAnswerChange([...finalExisting, newSelection]);
  };

  const handleTrimRemove = (selectionId: string) => {
    onAnswerChange(answer.filter((s) => s.id !== selectionId));
  };

  const handlePercentageChange = (selectionId: string, percentage: number) => {
    onAnswerChange(
      answer.map((s) =>
        s.id === selectionId
          ? { ...s, percentage: Math.max(0, Math.min(100, percentage)) }
          : s,
      ),
    );
  };

  // Max spec count to keep table columns consistent
  const maxSpecs = answer.reduce((max, sel) => {
    const trim = availableTrims.find((t) => t.id === sel.trimId);
    return trim
      ? Math.max(max, getItemSpecifications(catalog, trim.id).length)
      : max;
  }, 0);

  return (
    <Stack spacing={3}>
      {/* Selections table */}
      {answer.length > 0 && (
        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trim</TableCell>
                  {maxSpecs > 0 && (
                    <TableCell colSpan={maxSpecs}>Options</TableCell>
                  )}
                  <TableCell align="right">Percentage</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {answer.map((selection) => {
                  const trim = availableTrims.find(
                    (t) => t.id === selection.trimId,
                  );
                  if (!trim) return null;

                  const trimSpecs = getItemSpecifications(catalog, trim.id);

                  return (
                    <TableRow key={selection.id}>
                      {/* Trim name */}
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" fontWeight="medium">
                          {trim.title}
                        </Typography>
                      </TableCell>

                      {/* Spec columns */}
                      {Array.from({ length: maxSpecs }, (_, specIdx) => {
                        const spec = trimSpecs[specIdx];
                        if (!spec)
                          return <TableCell key={specIdx}>-</TableCell>;

                        const selValues = specValues[selection.id] || {};
                        const currentValue =
                          selValues[spec.id] || spec.defaultValue || '';

                        return (
                          <TableCell key={specIdx}>
                            <SpecField
                              spec={spec}
                              value={currentValue}
                              onChange={(v) =>
                                onSpecChange(selection.id, spec.id, v)
                              }
                              compact
                            />
                          </TableCell>
                        );
                      })}

                      {/* Percentage input */}
                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        <TextField
                          type="number"
                          size="small"
                          value={selection.percentage || 0}
                          onChange={(e) =>
                            handlePercentageChange(
                              selection.id,
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          InputProps={{
                            inputProps: { min: 0, max: 100, step: 1 },
                            endAdornment: '%',
                          }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>

                      {/* Remove */}
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleTrimRemove(selection.id)}
                        >
                          <Close />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Totals row */}
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      Total
                    </Typography>
                  </TableCell>
                  {maxSpecs > 0 && <TableCell colSpan={maxSpecs} />}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={totalPercentage === 100 ? 'success.main' : 'error'}
                    >
                      {totalPercentage.toFixed(0)}%
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Available trims to add */}
      <Box>
        <Stack spacing={2}>
          {availableTrims.map((trim) => (
            <Card
              key={trim.id}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                borderColor: 'divider',
                borderWidth: 2,
                outline: '2px solid transparent',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                minHeight: trim.imageUrl ? { xs: 120, md: 160 } : 80,
                '&:hover': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              }}
              onClick={() => handleTrimSelect(trim.id)}
            >
              <Stack
                direction="row"
                sx={{
                  cursor: 'pointer',
                  minHeight: trim.imageUrl ? { xs: 120, md: 160 } : 80,
                  transition: 'min-height 0.3s ease',
                }}
              >
                {trim.imageUrl && (
                  <Box
                    sx={{
                      width: { xs: 120, md: 160 },
                      minWidth: { xs: 120, md: 160 },
                      alignSelf: 'stretch',
                      position: 'relative',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={trim.imageUrl}
                      alt={trim.title || ''}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}

                <Stack
                  sx={{ flex: 1, minWidth: 0, p: 2 }}
                  justifyContent="flex-start"
                  spacing={0.5}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="h5">
                      {trim.title || 'Unnamed'}
                    </Typography>
                    <Button size="small" variant="outlined">
                      Add
                    </Button>
                  </Stack>
                  {trim.description && (
                    <Typography variant="body2" color="text.secondary">
                      {trim.description}
                    </Typography>
                  )}
                  {trim.pricing && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontWeight: 'medium' }}
                    >
                      {formatPricing(trim.pricing)}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      {/* Validation */}
      {question.requireExactTotal &&
        totalPercentage !== 100 &&
        answer.length > 0 && (
          <Typography variant="body2" color="error" textAlign="center">
            Percentages must total exactly 100%
          </Typography>
        )}

      {answer.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Select trims and assign percentages
        </Typography>
      )}
    </Stack>
  );
};
